// src/pages/ShootingTotals.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { Target, Loader2, CalendarDays, Download, Filter, Users } from "lucide-react";
import i18n from "../i18n";
// Charts
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { BackButton } from "../components/BackButton";


/* ========= mini-hook i18n (safe) ========= */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, { ...options, lng }))
        : String((i18n.t as any)(key, { lng })),
    [lng]
  );
  return { t, lng };
}

type EntryRow = {
  id: string;
  block_id: string;
  player_number: number;
  player_name: string;
  location: string;
  made: number;
  attempted: number;
};

type BlockRow = { id: string; training_id: string | null };
type TrainingRow = { id: string; date: string; user_id: string; team_id?: string | null };
type TeamRow = { id: string; team_name: string };

export default function ShootingTotals() {
  const { t, lng } = useI18nSafe();
  const locale = lng === "fr" ? "fr-FR" : "en-US";

  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<EntryRow[]>([]);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // Équipes
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(""); // "" = toutes

  // Historique (meta)
  const [trainingsMap, setTrainingsMap] = useState<Map<string, TrainingRow>>(new Map());
  const [blocksList, setBlocksList] = useState<BlockRow[]>([]);

  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" }) : "-";

  // ---- charge la liste des équipes de l’utilisateur
  useEffect(() => {
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = (auth as any)?.user?.id as string | undefined;
      if (!uid) {
        setTeams([]);
        return;
      }
      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name")
        .eq("user_id", uid)
        .order("team_name", { ascending: true });
      if (!error && data) setTeams(data);
    })();
  }, []);

  // ---- charge toutes les entrées de tir de l’utilisateur (option: filtres dates + équipe)
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const uid = (auth as any)?.user?.id as string | undefined;
      if (!uid) {
        setEntries([]);
        setBlocksList([]);
        setTrainingsMap(new Map());
        setLoading(false);
        return;
      }

      // 1) entraînements
      let trq = supabase
        .from("trainings")
        .select("id,date,user_id,team_id")
        .eq("user_id", uid)
        .order("date", { ascending: false }) as any;

      if (dateFrom) trq = trq.gte("date", dateFrom);
      if (dateTo) {
        const end = dateTo.length === 10 ? `${dateTo}T23:59:59` : dateTo;
        trq = trq.lte("date", end);
      }
      if (selectedTeamId) trq = trq.eq("team_id", selectedTeamId);

      const { data: trainings, error: tErr } = (await trq) as {
        data: TrainingRow[] | null;
        error: any;
      };
      if (tErr || !trainings?.length) {
        setEntries([]);
        setBlocksList([]);
        setTrainingsMap(new Map());
        setLoading(false);
        return;
      }
      const trainingIds = trainings.map((t) => t.id);

      // Map pour dates
      const tMap = new Map<string, TrainingRow>();
      trainings.forEach((t) => tMap.set(t.id, t));
      setTrainingsMap(tMap);

      // 2) blocs liés
      const { data: blocks, error: bErr } = await supabase
        .from("training_shoot_blocks")
        .select("id, training_id")
        .in("training_id", trainingIds) as { data: BlockRow[] | null; error: any };
      if (bErr || !blocks?.length) {
        setEntries([]);
        setBlocksList([]);
        setLoading(false);
        return;
      }
      setBlocksList(blocks);
      const blockIds = blocks.map((b) => b.id);

      // 3) entrées de tir
      const { data: rows, error: eErr } = await supabase
        .from("training_shoot_entries")
        .select("id, block_id, player_number, player_name, location, made, attempted")
        .in("block_id", blockIds)
        .order("created_at", { ascending: true }) as { data: EntryRow[] | null; error: any };

      if (eErr) {
        console.error(eErr);
        setEntries([]);
      } else {
        setEntries(rows || []);
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedTeamId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---- agrégations
  type Tot = { name: string; made: number; attempted: number; byLoc: Map<string, { made: number; attempted: number }> };

  // Total équipe (tous les joueurs affichés après filtres)
  const teamTotals = useMemo(() => {
    let made = 0;
    let attempted = 0;
    const byLoc = new Map<string, { made: number; attempted: number }>();

    for (const e of entries) {
      const m = e.made || 0;
      const a = e.attempted || 0;
      made += m;
      attempted += a;
      const key = e.location || "Other";
      const cur = byLoc.get(key) ?? { made: 0, attempted: 0 };
      cur.made += m;
      cur.attempted += a;
      byLoc.set(key, cur);
    }
    return { made, attempted, byLoc };
  }, [entries]);

  // Historique des blocs (date + totaux équipe par bloc)
  type BlockHistoryItem = { block_id: string; date: string | null; made: number; attempted: number };
  const blockHistory = useMemo<BlockHistoryItem[]>(() => {
    if (!blocksList.length) return [];
    const sums = new Map<string, { made: number; attempted: number }>();
    for (const e of entries) {
      const acc = sums.get(e.block_id) ?? { made: 0, attempted: 0 };
      acc.made += e.made || 0;
      acc.attempted += e.attempted || 0;
      sums.set(e.block_id, acc);
    }
    const arr: BlockHistoryItem[] = [];
    for (const b of blocksList) {
      const s = sums.get(b.id) || { made: 0, attempted: 0 };
      const tr = b.training_id ? trainingsMap.get(b.training_id) : undefined;
      arr.push({ block_id: b.id, date: tr?.date ?? null, made: s.made, attempted: s.attempted });
    }
    arr.sort((a, b) => {
      const da = a.date ? Date.parse(a.date) : 0;
      const db = b.date ? Date.parse(b.date) : 0;
      return db - da;
    });
    return arr;
  }, [blocksList, trainingsMap, entries]);

  // Par joueur
  const byPlayer = useMemo(() => {
    const m = new Map<number, Tot>();
    for (const r of entries) {
      const cur =
        m.get(r.player_number) ??
        { name: r.player_name || `#${r.player_number}`, made: 0, attempted: 0, byLoc: new Map() };

      cur.made += r.made || 0;
      cur.attempted += r.attempted || 0;

      const key = r.location || "Other";
      const agg = cur.byLoc.get(key) ?? { made: 0, attempted: 0 };
      agg.made += r.made || 0;
      agg.attempted += r.attempted || 0;
      cur.byLoc.set(key, agg);

      m.set(r.player_number, cur);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [entries]);

  const allLocations = useMemo(() => {
    const s = new Set<string>();
    entries.forEach((e) => s.add(e.location || "Other"));
    return [...s.values()].sort((a, b) => a.localeCompare(b, locale));
  }, [entries, locale]);

  // ---- export CSV
  const downloadCSV = () => {
    const head = ["number", "name", "made", "attempted", "pct", ...allLocations.flatMap((loc) => [`${loc}_made`, `${loc}_attempted`])];
    const lines: string[] = [head.join(",")];

    // (Optionnel) On pourrait ajouter une 1ère ligne "TOTAL" ici si tu veux

    byPlayer.forEach(([num, tot]) => {
      const pct = tot.attempted > 0 ? Math.round((tot.made / tot.attempted) * 100) : 0;
      const locCells: number[] = [];
      allLocations.forEach((loc) => {
        const v = tot.byLoc.get(loc) || { made: 0, attempted: 0 };
        locCells.push(v.made, v.attempted);
      });
      const row = [num, `"${tot.name.replace(/"/g, '""')}"`, tot.made, tot.attempted, pct, ...locCells].join(",");
      lines.push(row);
    });

    const suffix = selectedTeamId
      ? `_${(teams.find(t => t.id === selectedTeamId)?.team_name || "team").replace(/[^\w-]+/g, "_")}`
      : "_all";
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `shooting_totals${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

 return (
  <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
    {/* ===== HEADER STICKY ===== */}
    <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] flex items-center gap-3">
        <BackButton fallback="/dashboard" />

        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          <h1 className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
            {t("shootingtotals:title")}
          </h1>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={downloadCSV}
            disabled={byPlayer.length === 0}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-700
                       bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm disabled:opacity-60"
            title="CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        </div>
      </div>
    </div>

    {/* ===== CONTENU ===== */}
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 space-y-4">
      {/* Filtres */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3 font-semibold">
          <Filter className="w-4 h-4" />
          {t("shootingtotals:filters")}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          {/* Date from */}
          <label className="text-sm">
            <span className="block mb-1 flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <CalendarDays className="w-4 h-4" />
              {t("shootingtotals:from")}
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full lg:w-[180px] border rounded-xl px-3 py-2
                         bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
            />
          </label>

          {/* Date to */}
          <label className="text-sm">
            <span className="block mb-1 flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <CalendarDays className="w-4 h-4" />
              {t("shootingtotals:to")}
            </span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full lg:w-[180px] border rounded-xl px-3 py-2
                         bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
            />
          </label>

          {/* Team filter */}
          <label className="text-sm flex-1">
            <span className="block mb-1 flex items-center gap-1 text-gray-700 dark:text-gray-200">
              <Users className="w-4 h-4" />
              {t("shootingtotals:team")}
            </span>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full border rounded-xl px-3 py-2
                         bg-white dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
            >
              <option value="">{t("shootingtotals:all_teams")}</option>
              {teams.map((tm) => (
                <option key={tm.id} value={tm.id}>
                  {tm.team_name}
                </option>
              ))}
            </select>
          </label>

          <button
            onClick={loadData}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl
                       bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60
                       focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {t("shootingtotals:apply")}
          </button>
        </div>
      </div>

      {/* Camemberts équipe */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div className="font-semibold">{t("shootingtotals:team_pies_title")}</div>

          <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#10B981" }} />
              {t("shootingtotals:made")}
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: "#EF4444" }} />
              {t("shootingtotals:missed")}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* GLOBAL */}
          <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
            <div className="text-xs mb-1 font-semibold">{t("shootingtotals:total")}</div>
            <div className="h-[140px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: t("shootingtotals:made"), value: Math.max(0, teamTotals.made) },
                      { name: t("shootingtotals:missed"), value: Math.max(0, teamTotals.attempted - teamTotals.made) },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={35}
                    outerRadius={55}
                    stroke="none"
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#EF4444" />
                  </Pie>
                  <Tooltip
                    formatter={(val: number) => [val, ""]}
                    contentStyle={{ background: "var(--tw-prose-body,#111827)", color: "#fff", border: "none" }}
                    wrapperStyle={{ outline: "none" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-xs text-center text-gray-600 dark:text-gray-300">
              {teamTotals.attempted > 0
                ? `${Math.round((teamTotals.made / teamTotals.attempted) * 100)}%`
                : "0%"}
            </div>
          </div>

          {/* PAR ZONE */}
          {allLocations.map((loc) => {
            const v = teamTotals.byLoc.get(loc) || { made: 0, attempted: 0 };
            const missed = Math.max(0, v.attempted - v.made);
            const pct = v.attempted > 0 ? Math.round((v.made / v.attempted) * 100) : 0;
            return (
              <div key={loc} className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                <div className="text-xs mb-1 font-semibold truncate" title={loc}>{loc}</div>
                <div className="h-[140px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: t("shootingtotals:made"), value: Math.max(0, v.made) },
                          { name: t("shootingtotals:missed"), value: missed },
                        ]}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={35}
                        outerRadius={55}
                        stroke="none"
                      >
                        <Cell fill="#10B981" />
                        <Cell fill="#EF4444" />
                      </Pie>
                      <Tooltip
                        formatter={(val: number) => [val, ""]}
                        contentStyle={{ background: "var(--tw-prose-body,#111827)", color: "#fff", border: "none" }}
                        wrapperStyle={{ outline: "none" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-xs text-center text-gray-600 dark:text-gray-300">
                  {v.made} / {v.attempted} ({pct}%)
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Totaux par joueur */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 overflow-x-auto">
        <table className="min-w-[720px] text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-800">
              <th className="py-2 pr-2">{t("shootingtotals:player")}</th>
              <th className="py-2 px-2">{t("shootingtotals:total")}</th>
              <th className="py-2 px-2">%</th>
              {allLocations.map((loc) => (
                <th key={loc} className="py-2 px-2 whitespace-nowrap">{loc}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {byPlayer.length === 0 ? (
              <tr>
                <td colSpan={3 + allLocations.length} className="py-10 text-center text-gray-500">
                  {loading ? t("shootingtotals:loading") : t("shootingtotals:empty")}
                </td>
              </tr>
            ) : (
              <>
                {/* TOTAL ÉQUIPE */}
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 font-semibold">
                  <td className="py-2 pr-2">{t("shootingtotals:team_total")}</td>
                  <td className="py-2 px-2">{teamTotals.made} / {teamTotals.attempted}</td>
                  <td className="py-2 px-2">
                    {teamTotals.attempted > 0 ? Math.round((teamTotals.made / teamTotals.attempted) * 100) : 0}%
                  </td>
                  {allLocations.map((loc) => {
                    const v = teamTotals.byLoc.get(loc) || { made: 0, attempted: 0 };
                    const lpct = v.attempted > 0 ? Math.round((v.made / v.attempted) * 100) : 0;
                    return (
                      <td key={loc} className="py-2 px-2 whitespace-nowrap">
                        {v.made} / {v.attempted} ({lpct}%)
                      </td>
                    );
                  })}
                </tr>

                {/* JOUEURS */}
                {byPlayer.map(([num, tot]) => {
                  const pct = tot.attempted > 0 ? Math.round((tot.made / tot.attempted) * 100) : 0;
                  return (
                    <tr key={num} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-2">
                        {tot.name} <span className="text-xs text-gray-500">#{num}</span>
                      </td>
                      <td className="py-2 px-2">{tot.made} / {tot.attempted}</td>
                      <td className="py-2 px-2">{pct}%</td>
                      {allLocations.map((loc) => {
                        const v = tot.byLoc.get(loc) || { made: 0, attempted: 0 };
                        const lpct = v.attempted > 0 ? Math.round((v.made / v.attempted) * 100) : 0;
                        return (
                          <td key={loc} className="py-2 px-2 whitespace-nowrap">
                            {v.made} / {v.attempted} ({lpct}%)
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Historique des blocs */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3 font-semibold">
          <CalendarDays className="w-4 h-4" />
          {t("shootingtotals:blocks_history")}
        </div>

        {blockHistory.length === 0 ? (
          <div className="text-sm text-gray-500">
            {loading ? t("shootingtotals:loading") : t("shootingtotals:no_blocks")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[520px] text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 dark:border-gray-800">
                  <th className="py-2 px-2">{t("shootingtotals:date")}</th>
                  <th className="py-2 px-2">{t("shootingtotals:total")}</th>
                  <th className="py-2 px-2">%</th>
                </tr>
              </thead>
              <tbody>
                {blockHistory.map((b, idx) => {
                  const pct = b.attempted > 0 ? Math.round((b.made / b.attempted) * 100) : 0;
                  return (
                    <tr key={`${b.block_id}-${idx}`} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">{fmtDate(b.date)}</td>
                      <td className="py-2 px-2">{b.made} / {b.attempted}</td>
                      <td className="py-2 px-2">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
);

}
